import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'

/**
 * localStorage を AsyncStorage インターフェースに適合させたラッパー。
 *
 * `window.localStorage` は「サイトデータをブロックする設定」や容量超過で
 * **プロパティアクセス自体や各メソッドが同期的に例外を投げる**ことがある。
 * ライブラリ側は persistClient こそ try/catch しているが、
 * restoreClient / removeClient は保護していないため、ここで握り潰す（#3529）。
 *
 * 特に removeClient は言語切替時に LanguageDialog から呼んでおり、
 * 同期例外が飛ぶと後続の `window.location.reload()` に到達せず
 * 「言語を選んでも切り替わらない」状態になる。
 *
 * SSR / static export のビルド時は window が無いため undefined を渡す
 * （その場合 persister は no-op になる）。
 */
export const createSafeLocalStorage = (): {
  getItem: (key: string) => Promise<string | null>
  setItem: (key: string, value: string) => Promise<void>
  removeItem: (key: string) => Promise<void>
} => ({
  getItem: (key: string): Promise<string | null> => {
    try {
      return Promise.resolve(window.localStorage.getItem(key))
    } catch {
      // localStorage が使用不可。キャッシュ無しとして扱う
      return Promise.resolve(null)
    }
  },
  setItem: (key: string, value: string): Promise<void> => {
    try {
      window.localStorage.setItem(key, value)
    } catch {
      // 容量超過・使用不可。永続化を諦めるだけでアプリは継続する
    }
    return Promise.resolve()
  },
  removeItem: (key: string): Promise<void> => {
    try {
      window.localStorage.removeItem(key)
    } catch {
      // 使用不可。破棄できなくてもリロードは継続させる
    }
    return Promise.resolve()
  },
})

/**
 * TanStack Query の永続キャッシュ（localStorage / キー `REACT_QUERY_OFFLINE_CACHE`）。
 *
 * `pages/_app.tsx` の `PersistQueryClientProvider` に渡すほか、
 * 言語切替時に `LanguageDialog` から `removeClient()` を呼んで破棄するため、
 * モジュールスコープの単一インスタンスとしてここに切り出している（#3529）。
 *
 * queryKey にはロケールを含めているため表示自体は分離されるが、
 * 破棄しないと言語を切り替えるたびに言語数ぶん localStorage が積み上がる。
 */
export const queryPersister = createAsyncStoragePersister({
  storage: typeof window !== 'undefined' ? createSafeLocalStorage() : undefined,
})
